/**
 * @module scroll
 * @category widgets
 * @subcategory toolbox
 * @description Represents scroll component with next features:
 * 1. Allow to scroll element into view
 * 2. Allow to scroll element to provided coordinate
 * 3. Allow to scroll window to provided element
 * 4. Allow to scroll window to top
 * 5. Allow to get scroll position
 * 6. Allow to add callback on scroll event
 *
 * @example <caption>Example of scroll module usage</caption>
 * import { scrollWindowTo, scrollToTop } from 'widgets/toolbox/scroll';
 * scrollWindowTo(errorElement, true);
 */

import { debounce } from 'widgets/toolbox/debounce';
import { getViewType } from 'widgets/toolbox/viewtype';
import { stickyHeaderHeightMap } from 'config/stickyHeaderHeightMap';

/**
 * Returns object with element offset
 *
 * @param element - DOM element
 * @param all - true to calculate offset of the right and bottom corners
 * @returns page scroll position
 */
export function elementOffset(element: HTMLElement|undefined|null, all = false): { top: number; left: number; bottom: number; right: number } {
    const offset = {
        top: 0,
        left: 0,
        bottom: 0,
        right: 0
    };

    if (element) {
        const docElem = document.documentElement;
        const elemBox = element.getBoundingClientRect();

        offset.top = elemBox.top + window.pageYOffset - docElem.clientTop;
        offset.left = elemBox.left + window.pageXOffset - docElem.clientLeft;

        if (all) {
            offset.bottom = offset.top + element.clientHeight;
            offset.right = offset.left + element.clientWidth;
        }
    }

    return offset;
}

/**
 * Check if element is in viewport
 *
 * @param element - HTML Element
 * @param fullyVisible - true if element should be fully visible
 * @returns true if element is in viewport
 */
export function elementInViewport(element: HTMLElement|undefined, fullyVisible = false): boolean {
    if (!element) {
        return false;
    }

    const stickyHeaderHeight = stickyHeaderHeightMap[getViewType()];
    const eTop = elementOffset(element).top - stickyHeaderHeight;
    const eLeft = elementOffset(element).left;
    const eWidth = element.clientWidth;
    const eHeight = element.clientHeight;

    let wHeight = window.innerHeight;
    let wWidth = window.innerWidth;
    let wTop = window.pageYOffset;
    let wLeft = window.pageXOffset;

    if (wLeft === null) {
        wHeight = window.document.documentElement.clientHeight;
        wWidth = window.document.documentElement.clientWidth;
        wTop = window.document.documentElement.scrollTop;
        wLeft = window.document.documentElement.scrollLeft;
    }

    wHeight -= stickyHeaderHeight;

    if (fullyVisible) {
        return (
            eTop > wTop
            && eLeft > wLeft
            && (eTop + eHeight) < (wTop + wHeight)
            && (eLeft + eWidth) < (wLeft + wWidth)
        );
    }

    return (
        eTop < (wTop + wHeight)
        && eLeft < (wLeft + wWidth)
        && (eTop + eHeight) > wTop
        && (eLeft + eWidth) > wLeft
    );
}

/**
 * @description Scroll element into view
 * @param element - HTML Element
 */
export function scrollIntoView(element?: HTMLElement): void {
    if (!element) {
        return;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
}

/**
 * @description Scroll to element if it is not in the viewport
 * @param element - HTML Element
 */
export function scrollIntoViewport(element?: HTMLElement): void {
    if (!element) {
        return;
    }

    if (!elementInViewport(element, true)) {
        scrollIntoView(element);
    }
}

/**
 * @description Scroll element to provided coordinate
 * @param element - HTML Element
 * @param top - top coordinate
 * @param left - left coordinate
 */
export function scrollElementTo(element: HTMLElement, top = 0, left = 0): void {
    if (element && typeof element.scrollTo === 'function') {
        element.scrollTo({ top: top, left: left, behavior: 'smooth' });
    }
}

/**
 * @description Scroll window to provided element
 * @param element - HTML Element
 * @param isCountHeaderHeight - add to top header height
 * @param isCentered - centers the element inside the window
 */
export function scrollWindowTo(element?: HTMLElement, isCountHeaderHeight = false, isCentered = false): void {
    if (!element) {
        return;
    }

    const headerHeight = stickyHeaderHeightMap[getViewType()];
    let top = element.getBoundingClientRect().top + window.pageYOffset;

    if (isCentered) {
        top -= (window.innerHeight - element.offsetHeight) / 2;
    }

    const left = 0;

    if (isCountHeaderHeight) {
        window.scrollTo({ top: top - headerHeight, left: left, behavior: 'smooth' });
    } else {
        window.scrollTo({ top: top, left: left, behavior: 'smooth' });
    }
}

/**
 * @description Scroll window to top
 */
export function scrollToTop(): void {
    window.scrollTo(0, 0);
}

const DEBOUNCE_INTERVAL = 20;

type TScrollHandler = (arg0: { currentPosition: number; lastScrollPosition: number; diff: number }) => void;

const scrollHandlers: Array<TScrollHandler> = [];

/**
 * @description Get scroll position
 * @returns Scroll position
 */
export function getScrollPosition(): number {
    if (document.documentElement.scrollTop !== undefined) {
        return document.documentElement.scrollTop;
    }

    if (document.scrollingElement && document.scrollingElement.scrollTop !== undefined) {
        return document.scrollingElement.scrollTop;
    }

    return -1;
}

let lastScrollPosition = 0;

const debounceScroll = debounce(() => {
    const currentPosition = getScrollPosition();

    scrollHandlers.forEach(handler => handler({
        currentPosition,
        lastScrollPosition,
        diff: currentPosition - lastScrollPosition
    }));
    lastScrollPosition = currentPosition;
}, DEBOUNCE_INTERVAL, true, true);

/**
 * @description Add callback on scroll event
 * @param callback - on scroll callback
 * @returns result
 */
export function onScroll(callback: TScrollHandler): () => void {
    if (!scrollHandlers.length) {
        window.addEventListener('scroll', debounceScroll, { passive: true });
    }

    scrollHandlers.push(callback);

    return () => {
        scrollHandlers.splice(scrollHandlers.indexOf(callback), 1);

        if (!scrollHandlers.length) {
            window.removeEventListener('scroll', debounceScroll);
        }
    };
}
